import { useQuery, useReactiveVar } from '@apollo/client'
import { sortBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { BackToOutletListLink } from '@src/components/BackToOutletListLink/BackToOutletListLink'
import { getOutletSearchBySegmentVariables } from '@src/components/BusinessSegments/getSegmentOutletSearchVariables'
import { getBusinessSegmentOutletsDocument } from '@src/components/BusinessSegments/queries/__generated__/getBusinessSegmentOutlets.graphql-interface'
import { ErrorPage } from '@src/components/Errors/ErrorPage'
import { Description } from '@src/components/FeaturedOutlets/FeaturedOutlets.styles'
import { TextClamp } from '@src/components/Text/Clamp'
import { showAppBannerVar } from '@src/components/SiteHeader/utils/reactiveVar'
import { useCategories } from '@src/hooks/useCategories/useCategories'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { ALL_FULFILMENT_METHODS_SORTED } from '@src/utils/fulfilment'

import { CategoryFilteredOutlets } from '../OutletList/CategoryFilteredOutlets'
import { OutletCards } from '../OutletList/OutletCards'
import {
  HeaderContainer,
  OutletsOuterContainer,
} from '../OutletList/OutletList.styles'
import { SegmentOrderBy } from '@src/graphql-types'
import { parseOutletDates } from '@src/utils/fulfilmentTimes/parsers'
import { OutletListCompactSkeleton } from '../OutletList/OutletListCompactSkeleton'
import { useNavigate } from 'react-router-dom'

export const SegmentOutletList: React.FC = () => {
  const fulfilmentFilter = useFulfilmentFilter()
  const marketplace = useMarketplace()
  const { segmentId } = useParams<{ segmentId: string }>()
  const showAppBanner = useReactiveVar(showAppBannerVar)

  const { t } = useTranslation(['copy'])

  const { selectedCategories, setCategoryNames } = useCategories()
  const navigate = useNavigate()

  // gotta use state cause its not detecting selected date change
  const variables = segmentId
    ? getOutletSearchBySegmentVariables({
        fulfilmentFilterData: fulfilmentFilter.data,
        marketplaceId: marketplace.id,
        businessSegmentId: segmentId,
        limitOutlets: false,
      })
    : undefined

  const { data, loading, error } = useQuery(
    getBusinessSegmentOutletsDocument,
    variables ? { variables } : { skip: true }
  )

  if (loading) {
    return <OutletListCompactSkeleton />
  }
  if (error) {
    return <ErrorPage logError={error} />
  }
  if (!data) {
    return <ErrorPage />
  }

  const businessSegment = data.businessSegment
  const outlets = data.businessSegmentOutlets.map(
    ({ nextOpenDate, ...outlet }) =>
      parseOutletDates({
        nextOpenDate: new Date(nextOpenDate).toISOString(),
        ...outlet,
      })
  )

  const sortedOutlets =
    businessSegment.orderBy === SegmentOrderBy.ALPHABETICAL
      ? sortBy(outlets, 'displayName')
      : outlets

  const fulfilmentsTitle =
    fulfilmentFilter.data.nonEmptyFulfilmentMethods.length ===
      ALL_FULFILMENT_METHODS_SORTED.length || !businessSegment.filterSegment
      ? marketplace.allOutletsText || t('all')
      : fulfilmentFilter.data.nonEmptyFulfilmentMethods
          .map(fulfilment => t(fulfilment.toLowerCase()))
          .join(' and ')

  return selectedCategories.length ? (
    <CategoryFilteredOutlets
      outlets={sortedOutlets}
      loading={loading}
      data-test-id="outlet-segmentlist-container-categoryfiltered"
    />
  ) : (
    <OutletsOuterContainer
      showAppBanner={showAppBanner}
      data-test-id="outlet-segmentlist-container-main"
    >
      <BackToOutletListLink
        showContainer
        customOnClick={() => {
          setCategoryNames({ categories: undefined })
          navigate(`/${marketplace.urlPath}`)
        }}
        data-test-id="outlet-segmentlist-link-backtolist"
      />
      {sortedOutlets.length > 0 && (
        <HeaderContainer data-test-id="outlet-segmentlist-text-header">
          {` ${businessSegment.name ?? ''}
            ${` - `}${fulfilmentsTitle}`}
        </HeaderContainer>
      )}
      {!!businessSegment.description?.length && sortedOutlets.length > 0 && (
        <Description data-test-id="outlet-segmentlist-text-description">
          <TextClamp clamp={5}>{businessSegment.description}</TextClamp>
        </Description>
      )}
      <OutletCards
        outlets={sortedOutlets}
        data-test-id="outlet-segmentlist-container-cards"
      />
    </OutletsOuterContainer>
  )
}
